import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ShellSkin } from './types';
import { useIsPhone } from '../design/useMediaQuery';

// ShellProvider: owns the one piece of durable state the shell layer needs — which skin is
// active — persisted per browser in localStorage (same SSR-safe try/catch pattern as
// theme.tsx). It also carries the "window service": a bridge the Desktop skin registers so
// deep-in-the-tree screens (e.g. a grid's "Open in new window") can open a window without
// knowing anything about the window manager. Tabs are a per-skin concern (see useRecordTabs).

const KEY = 'hf.shell.skin';
export const DEFAULT_SKIN = 'workspace';
/**
 * The one skin that works on a phone. Desktop is a window manager (menu bar, draggable
 * windows, taskbar) and Classic is a fixed sidebar — neither survives a 390px viewport, so
 * below PHONE_QUERY the shell renders this regardless of what the viewer picked. The stored
 * preference is untouched: rotate to a tablet or open a laptop and their choice comes back.
 */
export const PHONE_SKIN = 'workspace';

export type WindowOpener = (path: string, title?: string) => void;

export interface ShellState {
  skin: string;
  setSkin: (id: string) => void;
  skins: ShellSkin[];
  /** True while the windowing (Desktop) skin is active. */
  isDesktop: boolean;
  /** True on a phone-sized viewport, where the skin choice is overridden and hidden. */
  isPhone: boolean;
  /** Present only when a windowing skin has registered one; opens a route in a new window. */
  openWindow?: WindowOpener;
  /** Skins call this on mount/unmount to publish (or clear) their window opener. */
  registerWindowOpener: (fn: WindowOpener | null) => void;
}

// A no-provider default keeps useShell() safe to call from any screen/test without wrapping.
const DEFAULT_STATE: ShellState = {
  skin: DEFAULT_SKIN,
  setSkin: () => undefined,
  skins: [],
  isDesktop: false,
  isPhone: false,
  registerWindowOpener: () => undefined,
};

const Ctx = createContext<ShellState>(DEFAULT_STATE);

function readSkin(fallback: string, skins: ShellSkin[]): string {
  try {
    const v = localStorage.getItem(KEY);
    if (v && skins.some((s) => s.id === v)) return v;
  } catch {
    /* storage unavailable */
  }
  return fallback;
}

export function ShellProvider({ skins, initialSkin = DEFAULT_SKIN, children }: { skins: ShellSkin[]; initialSkin?: string; children: ReactNode }) {
  const [skin, setSkinState] = useState<string>(() => readSkin(initialSkin, skins));
  const openerRef = useRef<WindowOpener | null>(null);
  const [opener, setOpener] = useState<WindowOpener | null>(null);

  const isPhone = useIsPhone();
  // Fall back to the default skin if the stored id is no longer registered.
  const stored = skins.some((s) => s.id === skin) ? skin : (skins.some((s) => s.id === initialSkin) ? initialSkin : (skins[0]?.id ?? DEFAULT_SKIN));
  // On a phone the choice is not the viewer's to make: only PHONE_SKIN fits. Their stored
  // preference is left alone and returns the moment the viewport is wide enough for it.
  const phoneSkin = skins.some((sk) => sk.id === PHONE_SKIN) ? PHONE_SKIN : stored;
  const resolved = isPhone ? phoneSkin : stored;

  const setSkin = useCallback((id: string) => {
    setSkinState(id);
    try {
      localStorage.setItem(KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const registerWindowOpener = useCallback((fn: WindowOpener | null) => {
    openerRef.current = fn;
    setOpener(() => fn);
  }, []);

  const value = useMemo<ShellState>(
    () => ({
      skin: resolved,
      setSkin,
      skins,
      isDesktop: resolved === 'desktop',
      isPhone,
      openWindow: resolved === 'desktop' ? opener ?? undefined : undefined,
      registerWindowOpener,
    }),
    [resolved, setSkin, skins, isPhone, opener, registerWindowOpener],
  );

  // Clear a stale opener when the skin changes away from a windowing skin.
  useEffect(() => {
    if (resolved !== 'desktop' && openerRef.current) {
      openerRef.current = null;
      setOpener(null);
    }
  }, [resolved]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useShell(): ShellState {
  return useContext(Ctx);
}
